/**
 * Compact card component that displays publishing status and Share & Publish button
 */
// Card components replaced with custom div elements
import { ExternalLink, Loader2, Rss, Clock, X, AlertTriangle, CalendarDays, Pause, Play } from 'lucide-react'
import { ShareContentDropdown } from './ShareContentDropdown'
import { Content } from '../useContentData'
import { Button } from '@/components/ui/button'
import { ChangeScheduleModal } from './ChangeScheduleModal'
import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/components/ui/use-toast'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'


interface PublishStatusCardProps {
    content: Content
    versionNumber?: number
    onShowPublishHelp: () => void
    onContentUpdate?: () => void
}



export function PublishStatusCard({ 
    content, 
    versionNumber,
    onShowPublishHelp,
    onContentUpdate
}: PublishStatusCardProps) {
    const scheduledPostData = content?.scheduledPostData
    const [changeScheduleModal, setChangeScheduleModal] = useState(false)
    const [showCancelDialog, setShowCancelDialog] = useState(false)
    const [isCanceling, setIsCanceling] = useState(false)
    const [isTogglingHold, setIsTogglingHold] = useState(false)
    const { toast } = useToast()
    
    // Debug: Log when component re-renders and what data it receives
    console.log('PublishStatusCard render:', {
        contentId: content?.id,
        hasScheduledPostData: !!scheduledPostData,
        scheduledPostStatus: scheduledPostData?.status,
        timestamp: new Date().toISOString()
    })

    // Check if RSS is published - TODO: Need to look up platform details
    const rssPost = null // Temporarily disabled until platform lookup is fixed

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'PUBLISHED':
                return 'bg-gradient-to-r from-emerald-100 to-green-200 dark:from-emerald-900 dark:to-green-800 text-emerald-800 dark:text-emerald-100 border border-emerald-300/50 shadow-sm'
            case 'SCHEDULED':
                return 'bg-gradient-to-r from-blue-100 to-indigo-200 dark:from-blue-900 dark:to-indigo-800 text-blue-800 dark:text-blue-100 border border-blue-300/50 shadow-sm'
            case 'HOLD':
            case 'HELD':
                return 'bg-gradient-to-r from-yellow-100 to-amber-200 dark:from-yellow-900 dark:to-amber-800 text-yellow-800 dark:text-yellow-100 border border-yellow-300/50 shadow-sm'
            case 'PROCESSING':
                return 'bg-gradient-to-r from-amber-100 to-yellow-200 dark:from-amber-900 dark:to-yellow-800 text-amber-800 dark:text-amber-100 border border-amber-300/50 shadow-sm'
            case 'CANCELLED':
                return 'bg-gradient-to-r from-gray-100 to-slate-200 dark:from-gray-800 dark:to-slate-700 text-gray-800 dark:text-gray-100 border border-gray-300/50 shadow-sm'
            case 'FAILED':
            case 'error':
                return 'bg-gradient-to-r from-red-100 to-rose-200 dark:from-red-900 dark:to-rose-800 text-red-800 dark:text-red-100 border border-red-300/50 shadow-sm'
            default:
                return 'bg-gradient-to-r from-gray-100 to-slate-200 dark:from-gray-800 dark:to-slate-700 text-gray-800 dark:text-gray-100 border border-gray-300/50 shadow-sm'
        }
    }

    const getStatusText = (status: string) => {
        switch (status) {
            case 'PUBLISHED':
                return 'Published'
            case 'SCHEDULED':
                return `Scheduled for ${content.scheduled_time ? new Date(content.scheduled_time).toLocaleString() : 'Unknown date'}`
            case 'HOLD':
            case 'HELD':
                return `On Hold`
            case 'PROCESSING':
                return 'Publishing...'
            case 'CANCELLED':
                return 'Unpublished' // Content was published and then unpublished
            case 'FAILED':
            case 'error':
                return 'Publishing Failed'
            default:
                return `Status: ${status}`
        }
    }

    const handleChangeSchedule = () => {
        setChangeScheduleModal(true)
    }

    const handleScheduleModalClose = () => {
        setChangeScheduleModal(false)
    }

    const handleScheduleUpdate = () => {
        // Call the parent's content update callback to refresh data
        onContentUpdate?.()
    }

    const handleCancelClick = () => {
        setShowCancelDialog(true)
    }

    const handleConfirmCancel = async () => {
        if (!scheduledPostData?.id) {
            toast({
                title: "Error",
                description: "No scheduled post found to cancel",
                variant: "destructive"
            })
            return
        }

        setIsCanceling(true)
        setShowCancelDialog(false)
        
        try {
            const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/api/autopost/cancel-publishing`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
                },
                body: JSON.stringify({
                    scheduledPostId: scheduledPostData.id,
                    force: true
                })
            })

            const data = await response.json()

            if (response.ok) {
                toast({
                    title: "Publishing Cancelled",
                    description: data.warning || "The publishing process has been cancelled.",
                    variant: data.warning ? "default" : "default"
                })
                
                // Refresh content data to show updated status
                onContentUpdate?.()
            } else {
                throw new Error(data.error || 'Failed to cancel publishing')
            }
            
        } catch (error) {
            console.error('Error canceling publishing:', error)
            toast({
                title: "Error",
                description: error instanceof Error ? error.message : "Failed to cancel publishing. Please try again.",
                variant: "destructive"
            })
        } finally {
            setIsCanceling(false)
        }
    }

    const handleCheckPlatform = () => {
        setShowCancelDialog(false)
        // TODO: Could add logic here to open the relevant social platform
        toast({
            title: "Check Platform",
            description: "Please check your social media platform directly to verify if the post was published",
        })
    }

    const handleToggleHold = async () => {
        if (!scheduledPostData?.id) {
            toast({
                title: "Error",
                description: "No scheduled post found",
                variant: "destructive"
            })
            return
        }

        setIsTogglingHold(true)
        
        try {
            const newStatus = content.display_status === 'SCHEDULED' ? 'HOLD' : 'SCHEDULED'
            
            // Update the scheduled post status directly in Supabase
            const { error } = await supabase
                .from('scheduled_social_posts')
                .update({ 
                    status: newStatus,
                    updated_at: new Date().toISOString()
                })
                .eq('id', scheduledPostData.id)

            if (error) {
                throw error
            }

            toast({
                title: newStatus === 'HOLD' ? "Post Put On Hold" : "Hold Removed",
                description: newStatus === 'HOLD' 
                    ? "This post will not be published until you remove the hold."
                    : "This post will be published at the scheduled time."
            })
            
            // Refresh content data to show updated status
            onContentUpdate?.()
            
        } catch (error) {
            console.error('Error toggling hold status:', error)
            toast({
                title: "Error",
                description: error instanceof Error ? error.message : "Failed to update hold status. Please try again.",
                variant: "destructive"
            })
        } finally {
            setIsTogglingHold(false)
        }
    }

    return (
        <div className="group relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/60 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105 w-80">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20" />
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-16 translate-x-16" />
            <div className="relative p-4 space-y-3">
                {/* Status Display */}
                {content.display_status ? (
                    <div className="space-y-2">
                        <div className={`text-sm rounded-full px-4 py-2 text-center font-medium transition-all duration-300 ${getStatusStyle(content.display_status)}`}>
                            {content.display_status === 'PROCESSING' ? (
                                <span className="flex items-center justify-center">
                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    {getStatusText(content.display_status)}
                                </span>
                            ) : (
                                getStatusText(content.display_status)
                            )}
                        </div>

                        {/* Enhanced Cancel Button - show for processing content */}
                        {content.display_status === 'PROCESSING' && (
                            <div className="flex justify-center">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCancelClick}
                                    disabled={isCanceling}
                                    className="text-xs bg-white/60 hover:bg-white/80 border-red-300/50 text-red-600 hover:text-red-700 hover:border-red-400/60 transition-all duration-300 transform hover:scale-105"
                                >
                                    {isCanceling ? (
                                        <>
                                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                            Canceling...
                                        </>
                                    ) : (
                                        <>
                                            <X className="h-3 w-3 mr-1" />
                                            Cancel
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}

                        {/* Published Date - show whenever content has a published_at date */}
                        {content.published_at && (
                            <div className="flex items-center justify-center gap-1 text-xs text-gray-600 bg-white/40 rounded-lg px-3 py-1.5 mt-2">
                                <CalendarDays className="h-3 w-3" />
                                <span>
                                    Published: {new Date(content.published_at).toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        day: 'numeric', 
                                        year: 'numeric' 
                                    })} at {new Date(content.published_at).toLocaleTimeString('en-US', { 
                                        hour: 'numeric', 
                                        minute: '2-digit',
                                        hour12: true 
                                    })}
                                </span>
                            </div>
                        )}

                        {/* Enhanced Schedule Management Buttons - show for scheduled and held content */}
                        {(content.display_status === 'SCHEDULED' || content.display_status === 'HOLD' || content.display_status === 'HELD') && (
                            <div className="flex justify-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleChangeSchedule}
                                    className="text-xs bg-white/60 hover:bg-white/80 border-blue-300/50 text-blue-600 hover:text-blue-700 hover:border-blue-400/60 transition-all duration-300 transform hover:scale-105"
                                >
                                    <Clock className="h-3 w-3 mr-1" />
                                    Change Schedule
                                </Button>
                                
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleToggleHold}
                                    disabled={isTogglingHold}
                                    className={`text-xs transition-all duration-300 transform hover:scale-105 ${
                                        (content.display_status === 'HOLD' || content.display_status === 'HELD')
                                            ? 'bg-green-50/60 hover:bg-green-100/80 border-green-300/50 text-green-600 hover:text-green-700 hover:border-green-400/60'
                                            : 'bg-yellow-50/60 hover:bg-yellow-100/80 border-yellow-300/50 text-yellow-600 hover:text-yellow-700 hover:border-yellow-400/60'
                                    }`}
                                >
                                    {isTogglingHold ? (
                                        <>
                                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                            {(content.display_status === 'HOLD' || content.display_status === 'HELD') ? 'Removing Hold...' : 'Holding...'}
                                        </>
                                    ) : (
                                        <>
                                            {(content.display_status === 'HOLD' || content.display_status === 'HELD') ? (
                                                <>
                                                    <Play className="h-3 w-3 mr-1" />
                                                    Stop Holding
                                                </>
                                            ) : (
                                                <>
                                                    <Pause className="h-3 w-3 mr-1" />
                                                    Hold This Post
                                                </>
                                            )}
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}

                        {/* Published URL - show when content has been published */}
                        {(content.display_status === 'PUBLISHED' || content.published_at) && (
                            <button 
                                onClick={() => {
                                    // For blog posts, navigate to internal brand blog post route
                                    if (content.content_type === 'blog_post') {
                                        window.open(`/brands/${content.brand_id}/blog/${content.id}`, '_blank');
                                    } else if (content.scheduledPostData?.published_url) {
                                        console.log('Opening published URL:', content.scheduledPostData.published_url);
                                        window.open(content.scheduledPostData.published_url, '_blank', 'noopener,noreferrer');
                                    }
                                }}
                                className="text-sm text-primary flex items-center justify-center hover:underline cursor-pointer"
                            >
                                View Published Post <ExternalLink className="h-3 w-3 ml-1" />
                            </button>
                        )}

                        {/* Error Display */}
                        {content.display_status === 'FAILED' && content.scheduledPostData?.error_message && (
                            <div className="text-xs p-2 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded">
                                <div className="font-medium text-red-800 dark:text-red-200 mb-1">
                                    Error:
                                </div>
                                <div className="text-red-700 dark:text-red-300">
                                    {content.scheduledPostData.error_message}
                                </div>
                                {content.scheduledPostData.attempt_count && content.scheduledPostData.attempt_count > 1 && (
                                    <div className="text-red-600 dark:text-red-400 mt-1">
                                        Failed after {content.scheduledPostData.attempt_count} attempts
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Cancelled/Unpublished Display */}
                        {content.display_status === 'CANCELLED' && (
                            <div className={`text-xs p-2 rounded ${
                                content.scheduledPostData?.error_message
                                    ? 'bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800'
                                    : 'bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700'
                            }`}>
                                <div className={`font-medium mb-1 flex items-center gap-1 ${
                                    content.scheduledPostData?.error_message
                                        ? 'text-red-800 dark:text-red-200'
                                        : 'text-gray-800 dark:text-gray-200'
                                }`}>
                                    {content.scheduledPostData?.error_message && (
                                        <AlertTriangle className="h-3 w-3" />
                                    )}
                                    Unpublished
                                </div>
                                <div className={
                                    content.scheduledPostData?.error_message
                                        ? 'text-red-700 dark:text-red-300 space-y-2'
                                        : 'text-gray-700 dark:text-gray-300'
                                }>
                                    {content.scheduledPostData?.error_message ? (
                                        <>
                                            <div>{content.scheduledPostData.error_message}</div>
                                            {content.scheduledPostData.error_message.toLowerCase().includes('duplicate') && (
                                                <div className="text-xs pt-1 border-t border-red-200 dark:border-red-800">
                                                    💡 <strong>Tip:</strong> This platform prevents republishing identical content. Try editing the post before republishing.
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        'This content was previously published and has been unpublished.'
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-2">
                        <div className="text-sm rounded-full px-4 py-2 text-center font-medium bg-gradient-to-r from-gray-100 to-slate-200 dark:from-gray-800 dark:to-slate-700 text-gray-800 dark:text-gray-100 border border-gray-300/50 shadow-sm transition-all duration-300">
                            Unpublished
                        </div>
                        <div className="text-xs text-muted-foreground text-center">
                            This content has not been published yet
                        </div>
                    </div>
                )}

                {/* RSS Feed URL Display */}
                {rssPost && content.brand_id && (
                    <div className="mt-3 p-3 bg-muted rounded-lg space-y-2">
                        <div className="flex items-center gap-2 text-sm font-medium">
                            <Rss className="h-4 w-4" />
                            RSS Feed
                        </div>
                        <div className="flex items-center gap-2">
                            <code className="text-xs bg-background px-2 py-1 rounded flex-1 overflow-x-auto">
                                {`${import.meta.env.VITE_WORKERS_URL}/blog-rss/${content.brand_id}`}
                            </code>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    navigator.clipboard.writeText(`${import.meta.env.VITE_WORKERS_URL}/blog-rss/${content.brand_id}`)
                                }}
                            >
                                Copy URL
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(`${import.meta.env.VITE_WORKERS_URL}/blog-rss/${content.brand_id}`, '_blank')}
                            >
                                Preview Feed
                            </Button>
                        </div>
                    </div>
                )}

                {/* Share & Publish Button */}
                <div className="flex justify-center">
                    <ShareContentDropdown 
                        contentId={content.id} 
                        contentType={content.content_type}
                        versionNumber={versionNumber || content.current_version}
                        onShowPublishHelp={onShowPublishHelp}
                        onContentUpdate={onContentUpdate}
                    />
                </div>

                {/* Change Schedule Modal */}
                <ChangeScheduleModal
                    isOpen={changeScheduleModal}
                    onClose={handleScheduleModalClose}
                    contentId={content.id}
                    currentScheduledTime={content.scheduled_time}
                    onScheduleUpdate={handleScheduleUpdate}
                />

                {/* Cancel Publishing Confirmation Dialog */}
                <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                    <AlertDialogContent className="sm:max-w-md">
                        <AlertDialogHeader>
                            <AlertDialogTitle className="flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-amber-500" />
                                Cancel Publishing?
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-left space-y-3 pt-2">
                                <p>
                                    This publishing process appears to be in progress.
                                </p>
                                <p>
                                    If the post has already been published on the platform, cancelling here won't remove it.
                                </p>
                                <p>
                                    This could result in duplicate posts if you try to publish again.
                                </p>
                                <p>
                                    Please check the platform directly before cancelling.
                                </p>
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                            <AlertDialogCancel 
                                onClick={handleCheckPlatform}
                                className="w-full sm:w-auto"
                            >
                                Check Platform First
                            </AlertDialogCancel>
                            <AlertDialogAction 
                                onClick={handleConfirmCancel}
                                disabled={isCanceling}
                                className="w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                                {isCanceling ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Cancelling...
                                    </>
                                ) : (
                                    'Force Cancel'
                                )}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </div>
    )
} 